// @ts-check

/**
 * @file Renders the timelog dashboard secondary filters modal.
 */

import FontAwesome6 from "@expo/vector-icons/FontAwesome6"
import {memo, useEffect} from "react"
import PropTypes from "prop-types"
import PropTypesExact from "prop-types-exact"
import {Pressable, Text, View} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import _ from "gettext-universal/build/src/translate.js"
import useLocale from "gettext-universal/build/src/use-locale-expo.js"

import Modal from "../../../components/modal"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @typedef {import("react").ComponentProps<typeof FontAwesome6>["name"]} FontAwesome6Name */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {"all" | "only_invoiced" | "only_uninvoiced"} TimelogDashboardStatusValue */

/**
 * @typedef {object} TimelogsDashboardMoreFiltersModalProps
 * @property {boolean} isVisible Whether the modal is visible.
 * @property {(statusValue: TimelogDashboardStatusValue) => unknown} onApply Apply callback.
 * @property {() => unknown} onClose Close callback.
 * @property {TimelogDashboardStatusValue} statusValue Current invoicing status filter.
 */

/**
 * @typedef {object} TimelogsDashboardMoreFiltersModalState
 * @property {TimelogDashboardStatusValue} draftStatusValue Draft status selection.
 */

/**
 * Displays secondary timelog filter controls.
 * @augments {ShapeComponent<TimelogsDashboardMoreFiltersModalProps, TimelogsDashboardMoreFiltersModalState>}
 */
class TimelogsDashboardMoreFiltersModal extends ShapeComponent {
  /** @type {TimelogsDashboardMoreFiltersModalState} */
  state = {
    draftStatusValue: "all"
  }
  static propTypes = PropTypesExact({
    isVisible: PropTypes.bool.isRequired,
    onApply: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    statusValue: PropTypes.oneOf(["all", "only_invoiced", "only_uninvoiced"])
      .isRequired
  })
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /**
   * Synchronizes draft status when the modal opens.
   * @returns {void} No return value.
   */
  setup() {
    useLocale()
    this.colors = useThemeColors()
    this.themeName = useThemeName()

    const isVisible = this.p.isVisible
    const statusValue = this.p.statusValue
    useEffect(() => {
      if (!isVisible) return
      this.s.draftStatusValue = statusValue
    }, [isVisible, statusValue])
  }

  /**
   * Renders the filters modal shell.
   * @returns {ReactNode} Modal content.
   */
  render() {
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <Modal
        cardStyle={styles.card ||= {
          gap: 16,
          maxWidth: 440,
          padding: 20
        }}
        isVisible={this.p.isVisible}
        onRequestClose={this.p.onClose}
        testID="timelogsDashboardScreen/moreFiltersModal"
      >
          <Text
            style={styles[`title-${themeName}`] ||= {
              color: colors.text,
              fontSize: 20,
              fontWeight: 700
            }}
            testID="timelogsDashboardScreen/moreFiltersModal/title"
          >
            {_("More filters")}
          </Text>
          <View
            style={styles.section ||= {
              gap: 8
            }}
            testID="timelogsDashboardScreen/moreFiltersModal/statusSection"
          >
            <Text
              style={styles[`label-${themeName}`] ||= {
                color: colors.textMuted,
                fontSize: 12,
                fontWeight: 700,
                textTransform: "uppercase"
              }}
              testID="timelogsDashboardScreen/moreFiltersModal/statusLabel"
            >
              {_("Invoicing status")}
            </Text>
            <StatusOptionButtonComponent
              iconName="list"
              isSelected={this.s.draftStatusValue === "all"}
              label={_("All")}
              onPress={this.tt.onAllStatusPress}
              testID="timelogsDashboardScreen/moreFiltersModal/status/all"
            />
            <StatusOptionButtonComponent
              iconName="file-circle-check"
              isSelected={this.s.draftStatusValue === "only_invoiced"}
              label={_("Invoiced only")}
              onPress={this.tt.onOnlyInvoicedStatusPress}
              testID="timelogsDashboardScreen/moreFiltersModal/status/onlyInvoiced"
            />
            <StatusOptionButtonComponent
              iconName="file-circle-xmark"
              isSelected={this.s.draftStatusValue === "only_uninvoiced"}
              label={_("Uninvoiced only")}
              onPress={this.tt.onOnlyUninvoicedStatusPress}
              testID="timelogsDashboardScreen/moreFiltersModal/status/onlyUninvoiced"
            />
          </View>
          <View
            style={styles.actions ||= {
              flexDirection: "row",
              gap: 10,
              justifyContent: "flex-end"
            }}
            testID="timelogsDashboardScreen/moreFiltersModal/actions"
          >
            <Pressable
              onPress={this.p.onClose}
              style={styles[`cancelButton-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.surfaceMuted,
                borderColor: colors.border,
                borderRadius: 10,
                borderWidth: 1,
                flexDirection: "row",
                gap: 8,
                paddingHorizontal: 14,
                paddingVertical: 10
              }}
              testID="timelogsDashboardScreen/moreFiltersModal/cancelButton"
            >
              <View
                style={styles.cancelButtonIcon ||= {
                  alignItems: "center",
                  justifyContent: "center",
                  width: 16
                }}
                testID="timelogsDashboardScreen/moreFiltersModal/cancelButton/icon"
              >
                <FontAwesome6 color={colors.text} name="xmark" size={14} />
              </View>
              <Text
                style={styles[`cancelButtonText-${themeName}`] ||= {
                  color: colors.text,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="timelogsDashboardScreen/moreFiltersModal/cancelButton/text"
              >
                {_("Cancel")}
              </Text>
            </Pressable>
            <Pressable
              onPress={this.tt.onApplyPress}
              style={styles[`applyButton-${themeName}`] ||= {
                alignItems: "center",
                backgroundColor: colors.primary,
                borderColor: colors.primary,
                borderRadius: 10,
                borderWidth: 1,
                flexDirection: "row",
                gap: 8,
                paddingHorizontal: 14,
                paddingVertical: 10
              }}
              testID="timelogsDashboardScreen/moreFiltersModal/applyButton"
            >
              <View
                style={styles.applyButtonIcon ||= {
                  alignItems: "center",
                  justifyContent: "center",
                  width: 16
                }}
                testID="timelogsDashboardScreen/moreFiltersModal/applyButton/icon"
              >
                <FontAwesome6 color={colors.onPrimary} name="check" size={14} />
              </View>
              <Text
                style={styles[`applyButtonText-${themeName}`] ||= {
                  color: colors.onPrimary,
                  fontSize: 14,
                  fontWeight: 600
                }}
                testID="timelogsDashboardScreen/moreFiltersModal/applyButton/text"
              >
                {_("Apply")}
              </Text>
            </Pressable>
          </View>
      </Modal>
    )
  }

  /**
   * Selects all timelogs.
   * @returns {void} No return value.
   */
  onAllStatusPress = () => {
    this.s.draftStatusValue = "all"
  }

  /**
   * Selects only invoiced timelogs.
   * @returns {void} No return value.
   */
  onOnlyInvoicedStatusPress = () => {
    this.s.draftStatusValue = "only_invoiced"
  }

  /**
   * Selects only uninvoiced timelogs.
   * @returns {void} No return value.
   */
  onOnlyUninvoicedStatusPress = () => {
    this.s.draftStatusValue = "only_uninvoiced"
  }

  /**
   * Applies the selected status filter.
   * @returns {void} No return value.
   */
  onApplyPress = () => {
    this.p.onApply(this.s.draftStatusValue)
  }
}

/** @type {NamedExoticComponent<TimelogsDashboardMoreFiltersModalProps>} */
const TimelogsDashboardMoreFiltersModalComponent = memo(shapeComponent(TimelogsDashboardMoreFiltersModal))

export default TimelogsDashboardMoreFiltersModalComponent

/**
 * @typedef {object} StatusOptionButtonProps
 * @property {FontAwesome6Name} iconName FontAwesome icon name.
 * @property {boolean} isSelected Whether the option is selected.
 * @property {string} label Option label.
 * @property {() => unknown} onPress Press callback.
 * @property {string} testID Base test id for the option.
 */

/** @typedef {Record<string, never>} StatusOptionButtonState */

/**
 * Displays one selectable status option.
 * @augments {ShapeComponent<StatusOptionButtonProps, StatusOptionButtonState>}
 */
class StatusOptionButton extends ShapeComponent {
  static propTypes = PropTypesExact({
    iconName: PropTypes.string.isRequired,
    isSelected: PropTypes.bool.isRequired,
    label: PropTypes.string.isRequired,
    onPress: PropTypes.func.isRequired,
    testID: PropTypes.string.isRequired
  })
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()

  /** Wires theme colors into the option. */
  setup() {
    this.colors = useThemeColors()
    this.themeName = useThemeName()
  }

  /**
   * Renders the status option button.
   * @returns {ReactNode} Status option content.
   */
  render() {
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <Pressable
        onPress={this.p.onPress}
        style={styles[`statusButton-${themeName}-${this.p.isSelected}`] ||= {
          alignItems: "center",
          backgroundColor: this.p.isSelected ? colors.chipActiveBackground : colors.surface,
          borderColor: this.p.isSelected ? colors.primary : colors.borderStrong,
          borderRadius: 10,
          borderWidth: 1,
          flexDirection: "row",
          paddingHorizontal: 14,
          paddingVertical: 12
        }}
        testID={this.p.testID}
      >
        <View
          style={styles.statusButtonIcon ||= {
            marginRight: 8,
            width: 14
          }}
          testID={`${this.p.testID}/icon`}
        >
          <FontAwesome6
            color={this.p.isSelected ? colors.chipActiveText : colors.text}
            name={this.p.iconName}
            size={13}
          />
        </View>
        <Text
          style={styles[`statusButtonText-${themeName}-${this.p.isSelected}`] ||= {
            color: this.p.isSelected ? colors.chipActiveText : colors.text,
            fontSize: 14,
            fontWeight: 600
          }}
          testID={`${this.p.testID}/label`}
        >
          {this.p.label}
        </Text>
      </Pressable>
    )
  }
}

/** @type {NamedExoticComponent<StatusOptionButtonProps>} */
const StatusOptionButtonComponent = memo(shapeComponent(StatusOptionButton))
